import { faker } from '@faker-js/faker';

import { coerceBoolean, isDefined, isEmpty, objectValues, random } from '@hsp/utilities';

import { defaultMockTime, Uri } from 'constants/index';
import { IPackageCode } from 'types/api';
import { IDataContainer, IResult } from 'types/api/common';
import { IDistrict } from 'types/api/districts';
import { IMailCode } from 'types/api/mail-codes';
import { IState } from 'types/api/states';
import { ITag } from 'types/api/tag';

import { BaseHttpService } from './http/base-http.service';

class LookupService extends BaseHttpService {
  constructor() {
    super({ endpoints: objectValues(Uri.Client) as string[] });
  }

  getDistricts(cancel = false): Promise<IResult<IDataContainer<IDistrict[]>>> | undefined {
    const url = Uri.Lookup.Districts;

    return this.get({
      signal: cancel ? this.cancellations[url].onCancellation().signal : undefined,
      url,
      withCredentials: true,
    });
  }

  getMailCodes(
    clients: string,
    packageCodes?: string,
    cancel = false
  ): Promise<IResult<IDataContainer<IMailCode[]>>> | undefined {
    const url = Uri.Lookup.MailCodes;

    const params = isDefined(packageCodes) && !isEmpty(packageCodes) ? { clients, packageCodes } : { clients };

    return this.get({
      params: params,
      signal: cancel ? this.cancellations[url].onCancellation().signal : undefined,
      url,
      withCredentials: true,
    });
  }

  getPackageCodes(clients: string, cancel = false): Promise<IResult<IPackageCode[]>> | undefined {
    const url = Uri.Lookup.PackageCodes;

    return this.get({
      params: { clients },
      signal: cancel ? this.cancellations[url].onCancellation().signal : undefined,
      url,
      withCredentials: true,
    });
  }

  getStates(cancel = false): Promise<IResult<IDataContainer<IState[]>>> | undefined {
    const url = Uri.Lookup.States;

    return this.get({
      signal: cancel ? this.cancellations[url].onCancellation().signal : undefined,
      url,
      withCredentials: true,
    });
  }

  getTags(cancel = false): Promise<IResult<ITag[]>> | undefined {
    const url = Uri.Lookup.Tags;

    return this.get({
      signal: cancel ? this.cancellations[url].onCancellation().signal : undefined,
      url,
      withCredentials: true,
    });
  }
}

export const lookupService = new LookupService();

if (coerceBoolean(process.env.REACT_APP_API_LOOKUP_FAKE)) {
  const mocked = lookupService.mock(defaultMockTime);
  const mock = mocked?.adapter;

  mock?.onGet(Uri.Client.Clients).reply((): Promise<[number, IResult<IDataContainer<IState[]>>]> => {
    return new Promise((resolve) => {
      const data: IState[] = [];

      Array.from({ length: random(100) }).forEach(() => {
        data.push({
          code: faker.string.alpha(2),
          id: faker.number.int(),
          name: faker.company.name(),
        });
      });

      const result: IResult<IDataContainer<IState[]>> = {
        dataContainer: { data },
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });
}
